import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Box,
  SpaceBetween,
  Button,
  Form,
  Header,
  Container,
  Icon,
  Alert,
  Select,
  Tabs,
  Table,
  Spinner,
  FormField,
  ColumnLayout,
  Toggle,
  ButtonDropdown,
} from "@amzn/awsui-components-react";
import {
  Ard,
  UslLine,
  ValidationError,
  FuseAccountingCentralDatastoreClient,
  CreateArdCommand,
  UpdateArdCommand,
  ValidateArdResponse,
  UseCase,
  UseCaseName,
} from "@amzn/fuse-accounting-central-datastore-client";
import { createValidateArdCommand } from "./validate-ard-command-builder";
import { getFacdConfig } from "src/config/fuse-accounting-central-datastore-config";
import { InitDomain } from "src/context/domain/domainContext";
import { useJemGen } from "src/context/jem-gen/jemGenContext";
import { Auth } from "aws-amplify";

// Import modular components
import { useValidationUtils } from "./components/ValidationUtils";
import { GeneralInformationSection } from "./components/GeneralInformationSection";
import { createTableColumnDefinitions } from "./components/TableComponents";
import {
  OverrideModal,
  UslLineDeleteModal,
  UseCaseDeleteModal,
  SuccessModal,
  FailureModal,
  SaveModal,
} from "./components/ConfirmationModals";
import { useFormDataManagement } from "./hooks/useFormDataManagement";
import { useUseCaseManagement } from "./hooks/useUseCaseManagement";
import { ModalMode } from "src/context/jem-gen/types";
import { Domain } from "src/config/BartLambdaConfig";
import { getDomainFromCountry } from "../constants";
import { isProd } from "src/util";

interface ArdEditModalProps {
  visible: boolean;
  onDismiss: () => void;
  onRefresh: () => Promise<void>;
  modalMode: ModalMode;
}

const ArdEditModal: React.FC<ArdEditModalProps> = ({
  visible,
  onDismiss,
  onRefresh,
  modalMode,
}) => {
  // State management
  const [selectedUseCaseName, setSelectedUseCaseName] = useState<string | null>(
    null,
  );
  const [activeTabId, setActiveTabId] = useState("0");
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  );
  const [showValidation, setShowValidation] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [saveReason, setSaveReason] = useState("");
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successArdId, setSuccessArdId] = useState("");
  const [failureErrorMessage, setFailureErrorMessage] = useState("");
  const isEditMode = modalMode === "edit";

  // ARD response state for audit history
  const [ardResponse, setArdResponse] = useState<any>(undefined);

  // Focus management for derived attribute highlighting
  const [focusedField, setFocusedField] = useState<{
    fieldName: string;
    useCaseName?: string | null;
    uslLineIndex?: number | null;
  } | null>(null);

  // Confirmation modal states
  const [showDeleteUslLineConfirm, setShowDeleteUslLineConfirm] =
    useState(false);
  const [showDeleteUseCaseConfirm, setShowDeleteUseCaseConfirm] =
    useState(false);
  const [showOtherUseCases, setShowOtherUseCases] = useState(false);
  const [pendingUslLineDeletion, setPendingUslLineDeletion] = useState<{
    useCaseIndex: number;
    lineIndex: number;
  } | null>(null);
  const [pendingUseCaseDeletion, setPendingUseCaseDeletion] = useState<{
    useCaseIndex: number;
    activeTabId: string;
  } | null>(null);

  // Hooks and utilities
  const domainData = useContext(InitDomain);
  const { existingArds, getFacdClient, selectedArdDetailed, ardRegionMap } =
    useJemGen();
  const validationUtils = useValidationUtils(validationErrors, focusedField);
  const {
    formData,
    setFormData,
    handleChange: originalHandleChange,
    handleUslLineChange: originalHandleUslLineChange,
  } = useFormDataManagement(selectedArdDetailed?.ard || null, modalMode);
  const originalUseCaseManagement = useUseCaseManagement(
    formData,
    setFormData,
    setActiveTabId,
  );

  // Focus handlers
  const handleFocus = (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => {
    setFocusedField({ fieldName, useCaseName, uslLineIndex });
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  // Reset only button states when form data changes (keep validation errors and alerts)
  const resetButtonStates = () => {
    setIsValidated(false);
  };

  // Wrapped handlers that reset button states on change
  const handleChange = (field: keyof Ard, value: string) => {
    originalHandleChange(field, value);
    resetButtonStates();
  };

  const handleUslLineChange = (
    useCaseIndex: number,
    lineIndex: number,
    field: keyof UslLine,
    value: string,
  ) => {
    originalHandleUslLineChange(useCaseIndex, lineIndex, field, value);
    resetButtonStates();
  };

  // Wrapped use case management functions that reset button states
  const useCaseManagement = {
    ...originalUseCaseManagement,
    addNewUseCase: (selectedUseCaseName: string) => {
      originalUseCaseManagement.addNewUseCase(selectedUseCaseName);
      resetButtonStates();
    },
    addUslLine: (useCaseIndex: number) => {
      originalUseCaseManagement.addUslLine(useCaseIndex);
      resetButtonStates();
    },
    duplicateUslLine: (useCaseIndex: number, lineIndex: number) => {
      originalUseCaseManagement.duplicateUslLine(useCaseIndex, lineIndex);
      resetButtonStates();
    },
    deleteUslLine: (useCaseIndex: number, lineIndex: number) => {
      originalUseCaseManagement.deleteUslLine(useCaseIndex, lineIndex);
      resetButtonStates();
    },
    deleteUseCase: (useCaseIndex: number, activeTabId: string) => {
      originalUseCaseManagement.deleteUseCase(useCaseIndex, activeTabId);
      resetButtonStates();
    },
    changeUseCaseName: (useCaseIndex: number, newName: UseCaseName) => {
      originalUseCaseManagement.changeUseCaseName(useCaseIndex, newName);
      resetButtonStates();
    },
    moveUseCaseLeft: (useCaseIndex: number) => {
      originalUseCaseManagement.moveUseCaseLeft(useCaseIndex);
      resetButtonStates();
    },
    moveUseCaseRight: (useCaseIndex: number) => {
      originalUseCaseManagement.moveUseCaseRight(useCaseIndex);
      resetButtonStates();
    },
  };

  // Handler for duplicating USL lines
  const handleDuplicateUslLine = (useCaseIndex: number, lineIndex: number) => {
    useCaseManagement.duplicateUslLine(useCaseIndex, lineIndex);
  };

  // Utility functions
  const getUniqueValues = (
    field: keyof Ard | keyof UslLine,
    isUslField = false,
  ) => {
    const values = new Set<string>();
    existingArds.forEach((ard) => {
      if (isUslField) {
        ard.usecases?.forEach((usecase) => {
          usecase.uslLines?.forEach((line) => {
            const value = line[field as keyof UslLine];
            if (value && typeof value === "string") values.add(value);
          });
        });
      } else {
        const value = ard[field as keyof Ard];
        if (value && typeof value === "string") values.add(value);
      }
    });
    return Array.from(values)
      .sort()
      .map((value) => ({ label: value, value }));
  };

  const useCaseHasErrors = (useCaseName: string) => {
    if (!showValidation) return false;
    return validationErrors.some((error) => error.useCase === useCaseName);
  };

  const getOverallErrorSummary = () => {
    if (!showValidation || validationErrors.length === 0) return null;

    const uniqueMessages = Array.from(
      new Set(validationErrors.map((error) => error.message)),
    );

    return (
      <Alert type="error" header="Validation Failed">
        <SpaceBetween direction="vertical" size="xs">
          <div>Please fix the following errors before submitting:</div>
          {uniqueMessages.map((message, index) => (
            <div key={index}>• {message}</div>
          ))}
          <div>
            Please visit{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://frames.finance-tech.a2z.com/browse"
            >
              FRAMES{" "}
              <span>
                <Icon name="external" />
              </span>
            </a>{" "}
            to browse latest accurate USL segments codes.
          </div>
        </SpaceBetween>
      </Alert>
    );
  };

  // Event handlers
  const handleValidate = async () => {
    if (formData) {
      console.log("Validating ARD:", formData);

      setIsValidating(true);

      const validateArdCommand = createValidateArdCommand(formData);

      try {
        const credentials = await Auth.currentCredentials();
        const essentialCredentials = Auth.essentialCredentials(credentials);

        // Get the region for this ARD and use the appropriate FACD client
        const validateArdRegion = isProd() ? "prod-FE" : "devo-FE";
        console.log(
          `Using region '${validateArdRegion}' for validateArd command (ARD ID: ${formData.id || "new"})`,
        );

        const facdClient = await getFacdClient(validateArdRegion);
        if (!facdClient) {
          throw new Error(
            `Failed to get FACD client for region '${validateArdRegion}'`,
          );
        }

        console.log(
          "Starting FACD validateArd operation with validateArdCommand:",
          validateArdCommand,
        );

        const response = await facdClient.send(validateArdCommand);
        console.log("Response received from facdClient:", response);

        const validateArdResponse = response as ValidateArdResponse;
        setValidationErrors(validateArdResponse.errors || []);
        setShowValidation(true);

        if (validateArdResponse.valid) {
          setValidationSuccess(true);
          setIsValidated(true);
          console.log("Validation succeeded:", validateArdResponse);
        } else {
          setValidationSuccess(false);
          setIsValidated(false);
          console.log("Validation failed:", validateArdResponse);
        }
      } catch (error) {
        console.error("An error occurred in validateArd:", error);
        setValidationErrors([
          { message: "Failed to perform Validation: " + error },
        ]);
        setShowValidation(true);
        setValidationSuccess(false);
        setIsValidated(false);
      } finally {
        setIsValidating(false);
        console.log("Validate ARD operation completed.");
      }
    }
  };

  const determineArdDomain: () => Domain = () =>
    (isEditMode && formData && formData.id && ardRegionMap.get(formData.id)) ||
    (formData &&
      formData.buyingCustomerCountryCode &&
      getDomainFromCountry(formData.buyingCustomerCountryCode)) ||
    domainData.domain;

  const handleSubmit = async (
    changeDescription: string,
    onComplete: () => void,
  ) => {
    if (!formData || !changeDescription.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the region for this ARD and use the appropriate FACD client
      const ardRegion: Domain = determineArdDomain();
      console.log(
        `Using region '${ardRegion}' for ${isEditMode ? "update" : "create"}Ard command (ARD ID: ${formData.id || "new"})`,
      );
      const facdClient = await getFacdClient(ardRegion);
      if (!facdClient) {
        throw new Error(`Failed to get FACD client for region '${ardRegion}'`);
      }

      let response;
      if (isEditMode && formData.id) {
        const updateCommand = new UpdateArdCommand({
          ardId: formData.id,
          clientId: "Fuse",
          changeDescription,
          product: formData.product,
          term: formData.term,
          template: formData.template,
          billingMode: formData.billingMode,
          businessCountryCode: formData.businessCountryCode,
          buyingCustomerCountryCode: formData.buyingCustomerCountryCode,
          functionalCurrency: formData.functionalCurrency,
          localCurrency: formData.localCurrency,
          usecases: formData.usecases,
        });
        console.log("Updating ARD:", updateCommand);
        response = await facdClient.send(updateCommand);
      } else {
        const createCommand = new CreateArdCommand({
          clientId: "Fuse",
          changeDescription,
          product: formData.product,
          term: formData.term,
          template: formData.template,
          billingMode: formData.billingMode,
          businessCountryCode: formData.businessCountryCode,
          buyingCustomerCountryCode: formData.buyingCustomerCountryCode,
          functionalCurrency: formData.functionalCurrency,
          localCurrency: formData.localCurrency,
          usecases: formData.usecases,
        });
        console.log("Creating ARD:", createCommand);
        response = await facdClient.send(createCommand);
      }

      console.log("Response received from facdClient:", response);

      // Extract ARD ID from response
      const ardId = response.ard?.id || formData.id || "Unknown";

      // Show success modal
      setSuccessArdId(ardId);
      setShowSuccessModal(true);
    } catch (error) {
      console.error("An error occurred during submission:", error);

      // Extract error message from the error response
      let errorMessage = "An unknown error occurred";

      // Check for ConflictException for both create and update ARD commands
      if (
        error &&
        typeof error === "object" &&
        "name" in error &&
        error.name === "ConflictException"
      ) {
        if (isEditMode) {
          errorMessage = `Cannot update because an ARD with the given billing mode "${formData.billingMode}", product "${formData.product}", and customer country code "${formData.buyingCustomerCountryCode}" exists`;
        } else {
          errorMessage = `ARD with product "${formData.product}", billing term "${formData.term}", and customer country code "${formData.buyingCustomerCountryCode}" already exists`;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      } else if (error && typeof error === "object" && "message" in error) {
        errorMessage = String(error.message);
      }

      // Show failure modal
      setFailureErrorMessage(errorMessage);
      setShowFailureModal(true);
    } finally {
      setIsSubmitting(false);
      onComplete();
    }
  };

  // Deletion handlers
  const handleDeleteUslLineRequest = (
    useCaseIndex: number,
    lineIndex: number,
  ) => {
    setPendingUslLineDeletion({ useCaseIndex, lineIndex });
    setShowDeleteUslLineConfirm(true);
  };

  const handleDeleteUseCaseRequest = (
    useCaseIndex: number,
    activeTabId: string,
  ) => {
    setPendingUseCaseDeletion({ useCaseIndex, activeTabId });
    setShowDeleteUseCaseConfirm(true);
  };

  const confirmDeleteUslLine = () => {
    if (pendingUslLineDeletion) {
      useCaseManagement.deleteUslLine(
        pendingUslLineDeletion.useCaseIndex,
        pendingUslLineDeletion.lineIndex,
      );
      setPendingUslLineDeletion(null);
      setShowDeleteUslLineConfirm(false);
    }
  };

  const confirmDeleteUseCase = () => {
    if (pendingUseCaseDeletion) {
      useCaseManagement.deleteUseCase(
        pendingUseCaseDeletion.useCaseIndex,
        pendingUseCaseDeletion.activeTabId,
      );
      setPendingUseCaseDeletion(null);
      setShowDeleteUseCaseConfirm(false);
    }
  };

  // Reset validation state when modal closes
  useEffect(() => {
    if (!visible) {
      setValidationErrors([]);
      setShowValidation(false);
      setValidationSuccess(false);
      setIsValidated(false);
      setOverrideReason("");
      setSaveReason("");
      setShowOverrideModal(false);
      setShowSaveModal(false);
      setIsSubmitting(false);
      setShowSuccessModal(false);
      setShowFailureModal(false);
      setSuccessArdId("");
      setFailureErrorMessage("");
      setArdResponse(undefined);
    }
  }, [visible]);

  if (!formData) {
    return null;
  }

  return (
    <>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        size="max"
        header={
          <Header
            description={
              determineArdDomain() === "Global"
                ? "ARD cannot be created without any customer country code selected"
                : `ARD will be ${isEditMode ? "edited" : "created"} in ${determineArdDomain()}.`
            }
          >
            {isEditMode ? "Edit ARD" : "Create New ARD"}
          </Header>
        }
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onDismiss}>
                Cancel
              </Button>
              <Button onClick={handleValidate} disabled={isValidating}>
                {isValidating ? "Validating..." : "Validate ARD"}
              </Button>
              <Button
                variant="normal"
                onClick={() => setShowOverrideModal(true)}
                disabled={!showValidation || validationSuccess || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Override & Submit"}
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowSaveModal(true)}
                disabled={!isValidated || !validationSuccess}
              >
                {isEditMode ? "Save Changes " : "Create New "}
                {isValidated && validationSuccess && (
                  <Icon name="status-positive" />
                )}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween direction="vertical" size="l">
            {isValidating && (
              <Container>
                <SpaceBetween
                  direction="horizontal"
                  size="s"
                  alignItems="center"
                >
                  <Spinner size="normal" />
                  <Box>Validating ARD...</Box>
                </SpaceBetween>
              </Container>
            )}
            {getOverallErrorSummary()}
            {validationSuccess && (
              <Alert type="success" header="Validation Successful">
                ARD validation completed successfully. All business rules have
                been satisfied.
              </Alert>
            )}

            <Container>
              <Header variant="h2">General Information</Header>
              <GeneralInformationSection
                formData={formData}
                handleChange={handleChange}
                getUniqueValues={getUniqueValues}
                validationUtils={validationUtils}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </Container>

            {formData.usecases && formData.usecases.length > 0 && (
              <Container>
                <Header
                  actions={
                    <Toggle
                      onChange={({ detail }) => {
                        setShowOtherUseCases(detail.checked);

                        // If hiding OTHER use cases, check if we need to change the active tab
                        if (!detail.checked && formData.usecases) {
                          const activeUseCase =
                            formData.usecases[parseInt(activeTabId)];
                          if (
                            activeUseCase &&
                            activeUseCase.name === UseCaseName.OTHER
                          ) {
                            // Find first non-OTHER tab to select instead
                            const firstVisibleTabIndex =
                              formData.usecases.findIndex(
                                (uc) => uc.name !== UseCaseName.OTHER,
                              );
                            if (firstVisibleTabIndex >= 0) {
                              setActiveTabId(firstVisibleTabIndex.toString());
                            }
                          }
                        }
                      }}
                      checked={showOtherUseCases}
                    >
                      Show other use cases
                    </Toggle>
                  }
                  variant="h2"
                >
                  Use Cases
                </Header>
                <Tabs
                  activeTabId={activeTabId}
                  onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                  tabs={[
                    ...formData.usecases
                      .map((useCase, originalIndex) => ({
                        ...useCase,
                        originalIndex,
                      }))
                      .filter(
                        (useCase) =>
                          showOtherUseCases || useCase.name !== "OTHER",
                      )
                      .map((useCase) => {
                        const useCaseIndex = useCase.originalIndex;
                        return {
                          id: useCaseIndex.toString(),
                          label: (
                            <span
                              style={{
                                color: useCaseHasErrors(useCase.name || "")
                                  ? "#d13212"
                                  : "inherit",
                                fontWeight: useCaseHasErrors(useCase.name || "")
                                  ? "bold"
                                  : "normal",
                              }}
                            >
                              {!useCase.name
                                ? `Use Case ${useCaseIndex + 1}`
                                : useCase.name === UseCaseName.OTHER
                                  ? `${useCase.name} (${useCase.altName})`
                                  : useCase.name}
                            </span>
                          ),
                          action: (
                            <Button
                              variant="icon"
                              iconName="remove"
                              onClick={() =>
                                handleDeleteUseCaseRequest(
                                  useCaseIndex,
                                  activeTabId,
                                )
                              }
                              ariaLabel="Delete use case"
                            />
                          ),
                          content: (
                            <SpaceBetween direction="vertical" size="l">
                              <Table
                                items={useCase.uslLines || []}
                                stickyColumns={{ first: 0, last: 1 }}
                                columnDefinitions={createTableColumnDefinitions(
                                  useCase,
                                  useCaseIndex,
                                  validationUtils,
                                  handleDuplicateUslLine,
                                  handleDeleteUslLineRequest,
                                  handleFocus,
                                  handleBlur,
                                )}
                                submitEdit={(item, column, newValue) => {
                                  const index =
                                    useCase.uslLines?.indexOf(item) || 0;
                                  handleUslLineChange(
                                    useCaseIndex,
                                    index,
                                    column.id as keyof UslLine,
                                    newValue as string,
                                  );
                                }}
                                variant="embedded"
                              />
                              <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                  onClick={() =>
                                    useCaseManagement.addUslLine(useCaseIndex)
                                  }
                                  iconName="add-plus"
                                >
                                  Add Line
                                </Button>
                                <Button
                                  onClick={() =>
                                    useCaseManagement.moveUseCaseLeft(
                                      useCaseIndex,
                                    )
                                  }
                                  iconName="angle-left"
                                  disabled={useCaseIndex === 0}
                                >
                                  Move Left
                                </Button>
                                <Button
                                  onClick={() =>
                                    useCaseManagement.moveUseCaseRight(
                                      useCaseIndex,
                                    )
                                  }
                                  iconName="angle-right"
                                  disabled={
                                    !formData.usecases ||
                                    useCaseIndex ===
                                      formData.usecases.length - 1
                                  }
                                >
                                  Move Right
                                </Button>
                                {useCase.name === UseCaseName.OTHER && (
                                  <ButtonDropdown
                                    items={useCaseManagement
                                      .getAvailableUseCaseNames()
                                      .map((type) => ({
                                        text: type,
                                        id: type,
                                      }))}
                                    onItemClick={({ detail }) => {
                                      useCaseManagement.changeUseCaseName(
                                        useCaseIndex,
                                        detail.id as UseCaseName,
                                      );
                                    }}
                                  >
                                    Change use case name to
                                  </ButtonDropdown>
                                )}
                              </SpaceBetween>
                            </SpaceBetween>
                          ),
                        };
                      }),
                    {
                      id: "add-new",
                      label: (
                        <span>
                          <Icon name="add-plus" />
                        </span>
                      ),
                      content: (
                        <SpaceBetween direction="vertical" size="m">
                          {useCaseManagement.getAvailableUseCaseNames()
                            .length === 0 ? (
                            <Alert type="warning">
                              All use case names already exist.
                            </Alert>
                          ) : (
                            <>
                              <FormField label="Select Use Case Name">
                                <Select
                                  selectedOption={
                                    selectedUseCaseName
                                      ? {
                                          label: selectedUseCaseName,
                                          value: selectedUseCaseName,
                                        }
                                      : null
                                  }
                                  onChange={({ detail }) =>
                                    setSelectedUseCaseName(
                                      detail.selectedOption?.value || null,
                                    )
                                  }
                                  options={useCaseManagement
                                    .getAvailableUseCaseNames()
                                    .map((type) => ({
                                      label: type,
                                      value: type,
                                    }))}
                                  placeholder="Choose a use case name"
                                />
                              </FormField>
                              <Button
                                onClick={() => {
                                  if (selectedUseCaseName) {
                                    useCaseManagement.addNewUseCase(
                                      selectedUseCaseName,
                                    );
                                    setSelectedUseCaseName(null);
                                  }
                                }}
                                disabled={!selectedUseCaseName}
                              >
                                Add Use Case
                              </Button>
                            </>
                          )}
                        </SpaceBetween>
                      ),
                    },
                  ]}
                />
              </Container>
            )}

            {(!formData.usecases || formData.usecases.length === 0) && (
              <Container>
                <Header variant="h2">Add New Use Case</Header>
                <SpaceBetween direction="vertical" size="m">
                  <FormField label="Select Use Case Name">
                    <Select
                      selectedOption={
                        selectedUseCaseName
                          ? {
                              label: selectedUseCaseName,
                              value: selectedUseCaseName,
                            }
                          : null
                      }
                      onChange={({ detail }) =>
                        setSelectedUseCaseName(
                          detail.selectedOption?.value || null,
                        )
                      }
                      options={useCaseManagement
                        .getAvailableUseCaseNames()
                        .map((type) => ({ label: type, value: type }))}
                      placeholder="Choose a use case name"
                    />
                  </FormField>
                  <Button
                    onClick={() => {
                      if (selectedUseCaseName) {
                        useCaseManagement.addNewUseCase(selectedUseCaseName);
                        setSelectedUseCaseName(null);
                      }
                    }}
                    disabled={!selectedUseCaseName}
                  >
                    Add Use Case
                  </Button>
                </SpaceBetween>
              </Container>
            )}
          </SpaceBetween>
        </Form>
      </Modal>

      {/* Confirmation Modals */}
      <OverrideModal
        visible={showOverrideModal}
        onDismiss={() => {
          setShowOverrideModal(false);
          setOverrideReason("");
        }}
        onSubmit={() =>
          handleSubmit(overrideReason, () => setShowOverrideModal(false))
        }
        overrideReason={overrideReason}
        setOverrideReason={setOverrideReason}
        validationErrors={validationErrors}
        isSubmitting={isSubmitting}
      />

      <SaveModal
        visible={showSaveModal}
        onDismiss={() => {
          setShowSaveModal(false);
          setSaveReason("");
        }}
        onSubmit={() => {
          if (isValidated && validationSuccess) {
            handleSubmit(saveReason, () => setShowSaveModal(false));
          }
        }}
        saveReason={saveReason}
        setSaveReason={setSaveReason}
        isEditMode={isEditMode}
      />

      <UslLineDeleteModal
        visible={showDeleteUslLineConfirm}
        onDismiss={() => {
          setPendingUslLineDeletion(null);
          setShowDeleteUslLineConfirm(false);
        }}
        onConfirm={confirmDeleteUslLine}
        formData={formData}
        pendingDeletion={pendingUslLineDeletion}
      />

      <UseCaseDeleteModal
        visible={showDeleteUseCaseConfirm}
        onDismiss={() => {
          setPendingUseCaseDeletion(null);
          setShowDeleteUseCaseConfirm(false);
        }}
        onConfirm={confirmDeleteUseCase}
        formData={formData}
        pendingDeletion={pendingUseCaseDeletion}
      />

      <SuccessModal
        visible={showSuccessModal}
        onDismiss={() => {
          setShowSuccessModal(false);
          onDismiss(); // Close the main modal after success
          onRefresh();
        }}
        ardId={successArdId}
        isEditMode={isEditMode}
        isOverride={overrideReason.trim() !== ""}
      />

      <FailureModal
        visible={showFailureModal}
        onDismiss={() => setShowFailureModal(false)}
        errorMessage={failureErrorMessage}
        isEditMode={isEditMode}
      />
    </>
  );
};

export default ArdEditModal;
